import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from "@mui/material/SnackbarContent";
import IconButton from "@mui/material/IconButton";
import CancelIcon from "@mui/icons-material/Cancel";
import Slide from "@mui/material/Slide";
import { SxProps } from "@mui/system";
interface SnackbarContextType {
  showSnackbar: (message: string, isError?: boolean) => void;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

function SlideTransition(props: any) {
  return <Slide {...props} direction="down" />;
}

const snackbarStyles: Record<string, SxProps> = {
  success: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  error: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);

  const showSnackbar = (message: string, isError: boolean = false) => {
    if (message === "No records Found!") {
      isError = true;
    }
    if (message === "You do not have the necessary access to this environment.") {
      isError = true;
    }

    setSnackbarMessage(message);
    setIsError(isError);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

    // Memoize the context value to avoid unnecessary re-renders
  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        sx={{ marginTop: "-23px" }}
      >
        <SnackbarContent
          sx={{
            ...(isError ? snackbarStyles.error : snackbarStyles.success),
            width: "50vw",
            maxWidth: "50vw",
            borderRadius: 3,
            minHeight: "50px",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            fontWeight: "bolder",
            boxSizing: "border-box",
          }}
          message={<span className="snackbarMessage">{snackbarMessage || "No message"}</span>}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleSnackbarClose}
            >
              <CancelIcon fontSize="medium" />
            </IconButton>
          }
        />
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
